import AbstractStrategy from './AbstractStrategy';
import {TestState, SubCategories, Question, Answer} from 'Models'; // Todo: Circular reference with Question: Find better solution!!!
import reduce from 'ramda/es/reduce'
import sortWith from 'ramda/es/sortWith'
import ascend from 'ramda/es/ascend'
import descend from 'ramda/es/descend'
import prop from 'ramda/es/prop'
import Api from 'Ajax'
import {setMarkedQuestionsCountTo, incMarkedQuestionsCount, decMarkedQuestionsCount} from "@features/Bookmarks/model"
import { checkApiResponseForAuth } from '@features/_common/errorHandler'
import { useQuestionsStore } from '../../pinia/stores/questions'

const log = x => { console.log(x); return x }
const sumQuestionDurations = reduce( (acc,question) => acc + question.duration(), 0);

const asNumber = (value, fallback = 0) => {
    const n = Number(value)
    return Number.isFinite(n) ? n : fallback
}

const insertedFromValue = (q) => {
    if (!q) return 0

    return asNumber(
        q.insertedFromSubCategoryID
            ?? q.inserted_from_sub_category_id
            ?? q.inserted_from_sub_categoryID
            ?? 0,
        0
    )
}

const isInsertedQuestionRow = (q, requestedSubCategoryID) => {
    const rowSubCategoryID = asNumber(q && q.subCategoryID, requestedSubCategoryID)
    return rowSubCategoryID !== requestedSubCategoryID || insertedFromValue(q) > 0
}


export class QuestionExerciseStrategy extends AbstractStrategy {
    constructor() { super(); }


    //#region Static Methods
        // Removed redundant create method - use Question.create() instead

        static questionsStore() { return useQuestionsStore() }

        static isLastQuestionReached()      {
            return TestState.isInResultsMode() ? Answer.isLastAnswer()
                                               : this.selectedIndex()+1 >= this.currentQuestionsCount()
        }

        static selectedIndex()              {
            return TestState.isInResultsMode() ? Answer.selectedUserAnswerIndex()
                                               : this.questionsStore().selectedQuestionIndex
        }
        static currentQuestionsCount()      { return this.questionsStore().currentQuestionsCount; }
        static currentQuestionsDuration()   { return sumQuestionDurations(this.currentQuestions()); }
        static currentQuestion()            { 
            const currentQuestionStr = JSON.stringify(this.questionsStore().currentQuestion);
            // console.log("🛒 currentQuestion() Current Question from Store:", typeof currentQuestionStr === 'string' ? currentQuestionStr.substring(0, 100) : '');
            return Question.create(this.questionsStore().currentQuestion); }
        static previousQuestion()           { return Question.create(this.questionsStore().previousQuestion); }
        static currentQuestions()           { return this.questionsStore().currentQuestions.map(q=>Question.create(q)); }
        static currentQuestionsSubCategories()   { return this.questionsStore()
                                                              .allLoadedQuestions
                                                              .map(q=> SubCategories.createFromId(q.subCategoryID)) };

        static allQuestions()               { return this.questionsStore().allLoadedQuestions }
        static allQuestionsCount()           { return this.questionsStore().allLoadedQuestions.length }

        static doQuestionsExistFor(mainCategoryID, subCategoryID) {
            return this.questionsStore().doQuestionsExistFor(mainCategoryID,subCategoryID);
        }

        static isFirstQuestionOverall()             { return this.selectedIndex()===0}
        static ignoreCurrentQuestion()              { Answer.setToIgnoredById(this.currentQuestion().id()); }
        static incSelectedIndex()                   { this.questionsStore().incQuestionIndex(); }
        static decSelectedIndex()                   { this.questionsStore().decQuestionIndex(); }
        static setSelectedIndexTo(index)            { this.questionsStore().setQuestionIndexTo(index); }
        static setCurrentQuestions(questionSources) { this.questionsStore().setCurrentQuestions(questionSources) }
        static setActiveQuestions(questionSources)  { this.questionsStore().setActiveQuestions(questionSources) }

        static markByID(id, data)     {
            this.questionsStore().markQuestion(id)
            incMarkedQuestionsCount()
            return Api.postMarkQuestionWith(true, id, data)
                      .then( ({data}) => setMarkedQuestionsCountTo(data) )
                      .catch( err => {
                          this.questionsStore().unmarkQuestion(id)
                          decMarkedQuestionsCount()
                          checkApiResponseForAuth(AbstractStrategy.ebus(), err)
                      })
        }
        static unmarkByID(id, data)   {
            this.questionsStore().unmarkQuestion(id)
            decMarkedQuestionsCount()
            return Api.postMarkQuestionWith(false, id, data)
                      .then( ({data}) => setMarkedQuestionsCountTo(data) )
                      .catch( err => {
                          this.questionsStore().markQuestion(id)
                          incMarkedQuestionsCount()
                          checkApiResponseForAuth(AbstractStrategy.ebus(), err)
                      })
        }

        static resetAll()             { this.questionsStore().resetAll() }




    /**
     *
     * @param number mainCategoryID
     * @param number subCategoryID
     * @param array[ QuestionModel ] questionModels
     *
     * save all questions for select subcat (maincat, prof) in localstore
     */
        static addToAllQuestions(mainCategoryID, subCategoryID, questionModels) {
            let questions = questionModels.map(q=>q.getSource())
            this.questionsStore().addToAllQuestions({ mainCategoryID, subCategoryID, questions })
        }

        static loadAsCurrentQuestionsFor(professionID, mainCategoryID, subCategoryID, hasBrowserReloaded=false) {
            if (this.doQuestionsExistFor(mainCategoryID, subCategoryID)) {
                this.loadAsCurrentQuestionsFromLocalFor(mainCategoryID, subCategoryID, hasBrowserReloaded)
                return Promise.resolve()
            }

            // Todo: Better error handling
            return  Api.getQuestionsFor(professionID, mainCategoryID, subCategoryID)
                    .then(({data}) => {
                        const requestedSubCategoryID = Number(subCategoryID)
                        const questionData = Array.isArray(data)
                            ? data
                            : (data && Array.isArray(data.questions) ? data.questions : [])
                        const contextSubCategories = Array.isArray(data)
                            ? []
                            : (data && Array.isArray(data.contextSubCategories) ? data.contextSubCategories : [])

                        if (contextSubCategories.length > 0)
                            SubCategories.mergeKnownSubCategories(contextSubCategories)

                        // Keep exercise flow stable: in-between (inserted) questions must come first.
                        const insertedQuestions = questionData.filter((q) => isInsertedQuestionRow(q, requestedSubCategoryID))
                        const sourceQuestions = questionData.filter((q) => !isInsertedQuestionRow(q, requestedSubCategoryID))
                        const orderedQuestionData = insertedQuestions.concat(sourceQuestions)

                        const rows = orderedQuestionData.map((q, idx) => ({
                            idx,
                            id: q.id,
                            block: q.block,
                            subCategoryID: Number(q.subCategoryID || subCategoryID),
                            mainCategoryID: Number(q.mainCategoryID || mainCategoryID),
                            difficult: q.schwierigkeit,
                        }))
                        const countsBySubCategory = rows.reduce((acc, row) => {
                            const key = row.subCategoryID
                            acc[key] = (acc[key] || 0) + 1
                            return acc
                        }, {})

                        if (typeof window !== 'undefined' && window.appDebug) {
                            console.log('[ExerciseQuestions] API response received', {
                                professionID,
                                mainCategoryID,
                                subCategoryID,
                                questionCount: rows.length,
                                insertedQuestionCount: insertedQuestions.length,
                                sourceQuestionCount: sourceQuestions.length,
                                countsBySubCategory,
                            })
                        }

                        //let sort = sortWith([ascend(prop('block')), descend(prop('schwierigkeit'))])

                        // this.addToAllQuestions(mainCategoryID, subCategoryID, sort(orderedQuestionData).map(q=>Question.create(q)))
                        this.addToAllQuestions(mainCategoryID, subCategoryID, orderedQuestionData.map(q=>Question.create(q)))
                        this.loadAsCurrentQuestionsFromLocalFor(mainCategoryID, subCategoryID, hasBrowserReloaded)
                    })
                    .catch(err => checkApiResponseForAuth(AbstractStrategy.ebus(), err));
        }

        static loadAsCurrentQuestionsFromLocalFor(mainCategoryID, subCategoryID, hasBrowserReloaded=false) {
            this.questionsStore().loadQuestionsFor({ mainCategoryID, subCategoryID, hasBrowserReloaded });
            if(!hasBrowserReloaded)
                this.questionsStore().resetQuestionIndex();

            const selectedSubCategory = SubCategories.getSelected()
            if (
                selectedSubCategory
                && !selectedSubCategory.isEmpty()
                && Number(selectedSubCategory.id()) === Number(subCategoryID)
            ) {
                selectedSubCategory.updateAnsweredQuestionsStates()
            }
        }
    //#endregion Static Methods
}

export default QuestionExerciseStrategy;